import { Howl, Howler } from 'howler';
import { getSignedUrl } from './getSignedUrl';

// Audio player singleton
class AudioService {
  constructor() {
    this.currentSound = null;
    this.currentSongId = null;
    this.sounds = new Map();
    this.isRefreshing = new Map(); // Tracks refresh attempts for songIds
    this.callbacks = {
      onPlay: () => {},
      onPause: () => {},
      onEnd: () => {},
      onLoad: () => {},
      onSeek: () => {},
      onProgress: () => {},
      onError: () => {} // Added onError callback
    };
    
    // Update progress interval
    this.progressInterval = null;
    this.lastProgressUpdate = 0;
    this.progressThrottleMs = 250; // Throttle progress updates to 4 times per second
    this.lastAttemptedPositions = new Map(); // Added: Store last attempted position for retry
  }

  /**
   * Register event callbacks
   * @param {Object} callbacks - Callback functions
   */
  registerCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Load a song but don't play it - with debouncing
   * @param {string} songId - Song ID
   * @param {string} filePath - Path to the song file
   * @returns {Promise<boolean>} - Success status
   */
  async loadSong(songId, filePath) {
    return new Promise(async (resolve, reject) => {
      const existingSoundData = this.sounds.get(songId);
      if (existingSoundData && !this.isRefreshing.get(songId)) {
        const existingSound = existingSoundData.sound;
        const currentState = existingSound.state();
        if (currentState === 'loaded') {
          resolve(true); // Already loaded
          return;
        } else if (currentState === 'loading') {
          // If it's already loading, a more robust solution would be a map of loading promises.
          // For now, we'll unload the 'loading' one and proceed to create a new instance.
          console.warn(`Sound ${songId} is currently loading. Re-initiating load sequence.`);
          existingSound.unload(); 
          this.sounds.delete(songId);
        }
      }

      try {
        const signedUrl = await getSignedUrl(filePath);
        if (!signedUrl) {
          console.error(`Failed to get signed URL for ${songId}`);
          return reject(new Error('Failed to get signed URL'));
        }

        // Ensure any previous instance for this songId is cleaned up
        if (this.sounds.has(songId)) {
            const oldSoundData = this.sounds.get(songId);
            if (oldSoundData.sound) {
                oldSoundData.sound.unload();
            }
            this.sounds.delete(songId);
        }
        
        const sound = new Howl({
          src: [signedUrl],
          html5: false,
          preload: true,
          format: ['mp3'],
          onload: () => {
            console.log(`Howler onload for ${songId}`);
            this.sounds.set(songId, { sound, url: signedUrl, filePath });
            // If this load was part of a successful refresh, clear the flag.
            if (this.isRefreshing.get(songId)) {
                console.log(`Refresh load successful for ${songId}.`);
                this.isRefreshing.delete(songId);
            }
            this.callbacks.onLoad(songId);
            resolve(true);
          },
          onplay: () => {
            console.log(`Howler onplay for ${songId}`);
            // If this play was part of a successful refresh, clear the flag.
            if (this.isRefreshing.get(songId) && this.currentSongId === songId) {
                console.log(`Refresh play successful for ${songId}.`);
                this.isRefreshing.delete(songId);
            }
            this.callbacks.onPlay(songId); 
            requestAnimationFrame(() => this.startProgressTracking());
          },
          onpause: () => {
            console.log(`Howler onpause for ${songId}`);
            requestAnimationFrame(() => this.callbacks.onPause(songId));
          },
          onend: () => {
            console.log(`Howler onend for ${songId}`);
            this.stopProgressTracking();
            requestAnimationFrame(() => this.callbacks.onEnd(songId));
          },
          onseek: () => {
            console.log(`Howler onseek for ${songId}`);
            requestAnimationFrame(() => {
              const currentSeek = sound.seek();
              const duration = sound.duration();
              const progressPercentage = duration ? (currentSeek / duration) * 100 : 0;
              this.callbacks.onSeek(songId, progressPercentage);
            });
          },
          onloaderror: (instanceId, error) => {
            console.error(`Howler load error for ${songId}:`, error);
            this.callbacks.onError(songId, 'load', error);
            if (!this.isRefreshing.get(songId)) {
              this.handlePlaybackError(songId, filePath, 'load');
            } else {
               // Load error happened during a refresh attempt. This attempt failed.
               console.warn(`Load error during refresh attempt for ${songId}. Refresh failed.`);
               this.isRefreshing.delete(songId); // Clear flag, refresh failed.
            }
            reject(new Error(`Howler load error: ${error.message || error}`));
          },
          onplayerror: (instanceId, error) => {
            console.error(`Howler play error for ${songId}:`, error);
            this.callbacks.onError(songId, 'play', error);
            // filePath might not be directly available to this Howler callback.
            // Get it from the stored sound data if possible.
            const songData = this.sounds.get(songId);
            const currentFilePath = songData ? songData.filePath : filePath; // filePath is from loadSong's closure

            if (!this.isRefreshing.get(songId)) {
              this.handlePlaybackError(songId, currentFilePath || filePath, 'play');
            } else {
               // Play error happened during a refresh attempt. This attempt failed.
               console.warn(`Play error during refresh attempt for ${songId}. Refresh failed.`);
               this.isRefreshing.delete(songId); // Clear flag, refresh failed.
            }
          }
        });
      } catch (error) {
        console.error(`Error in loadSong setup for ${songId}:`, error);
        this.isRefreshing.delete(songId);
        reject(error);
      }
    });
  }

  /**
   * Play a song
   * @param {string} songId - Song ID
   * @param {string} filePath - Path to song file (used if song not already loaded)
   * @param {number} [position=0] - Start position in percentage
   * @param {boolean} [isRetry=false] - Internal flag for retry attempts
   * @returns {Promise<boolean>} - Success status
   */
  async playSong(songId, filePath, position = 0, isRetry = false) {
    console.log('[audioService] playSong called with:', {
      songId,
      position,
      isRetry,
      currentSongId: this.currentSongId,
      positionType: typeof position,
      isCurrentSound: this.currentSound ? 'exists' : 'null',
    });
    
    // Store the intended position for this attempt (used by handlePlaybackError).
    this.lastAttemptedPositions.set(songId, position);

    if (!isRetry) {
      // If this is a fresh, user-initiated play, clear any old refresh flag for this song.
      this.isRefreshing.delete(songId);
    }

    try {
      if (this.currentSongId && this.currentSongId !== songId && this.currentSound) {
        console.log('[audioService] Pausing previous song:', this.currentSongId);
        this.currentSound.pause();
      }
      
      const songDataFromMap = this.sounds.get(songId);
      const soundIsAlreadyLoaded = songDataFromMap && songDataFromMap.sound && songDataFromMap.sound.state() === 'loaded';
      
      console.log('[audioService] Sound status:', {
        soundIsAlreadyLoaded,
        isRefreshing: this.isRefreshing.get(songId) || false,
        soundState: songDataFromMap?.sound?.state() || 'no sound'
      });
      
      let soundToPlay;

      if (soundIsAlreadyLoaded && !this.isRefreshing.get(songId)) {
        console.log(`[audioService] Playing existing loaded sound for ${songId}`);
        soundToPlay = songDataFromMap.sound;
      } else {
        console.log(`[audioService] Loading sound for ${songId} before playing.`);
        const pathToLoad = filePath || (songDataFromMap && songDataFromMap.filePath);
        if (!pathToLoad) {
          console.error(`[audioService] Cannot load song ${songId}: filePath is missing for loadSong.`);
          return false; 
        }
        await this.loadSong(songId, pathToLoad); 
        
        const newlyLoadedSoundData = this.sounds.get(songId);
        if (!newlyLoadedSoundData || !newlyLoadedSoundData.sound) {
            console.error(`[audioService] Sound object for ${songId} not found after loadSong resolved.`);
            return false;
        }
        soundToPlay = newlyLoadedSoundData.sound;
      }
      
      if (!soundToPlay) {
        console.error(`[audioService] No sound object available for ${songId} to play.`);
        return false;
      }

      this.currentSongId = songId;
      this.currentSound = soundToPlay;
      
      const duration = soundToPlay.duration();
      console.log(`[audioService] Sound duration for ${songId}:`, duration, 'seconds');
      
      // Start playing first
      const playId = soundToPlay.play();
      if(!playId && soundToPlay.state() !== 'loaded') {
        console.warn(`[audioService] Howler play() called on ${songId} while not fully loaded. It will auto-load and play.`);
      } else if (!playId) {
        console.error(`[audioService] Howler sound.play() returned no playId for ${songId} but state is ${soundToPlay.state()}.`);
        return false;
      } else {
        console.log(`[audioService] Play command issued for ${songId} with playId ${playId}. Waiting for Howler's onplay event.`);
      }
      
      // CHANGED: Improved seeking after playback starts with better timeout and retry mechanism
      if (position > 0 && duration && Number.isFinite(duration)) {
        const seekToTime = (position / 100) * duration;
        
        // Enhanced seeking with retries
        const seekWithRetries = async (maxRetries = 3, delayBetweenRetries = 300) => {
          let retryCount = 0;
          
          const attemptSeek = async () => {
            // First wait to ensure playback has started
            await new Promise(resolve => setTimeout(resolve, 200 + (retryCount * 100)));
            
            // Check if we can still seek
            if (!this.currentSound || this.currentSongId !== songId) {
              console.log(`[audioService] Seek aborted - current song changed or sound no longer available`);
              return false;
            }
            
            console.log(`[audioService] Seeking ${songId} to ${seekToTime}s (${position}%) - attempt ${retryCount + 1}`);
            this.currentSound.seek(seekToTime);
            
            // Verify seek was successful
            await new Promise(resolve => setTimeout(resolve, 200));
            
            if (this.currentSound) {
              const actualPosition = this.currentSound.seek();
              const actualPercentage = (actualPosition / duration) * 100;
              const difference = Math.abs(seekToTime - actualPosition);
              const seekSuccessful = difference < 0.5 || actualPosition > 0.5;
              
              console.log(`[audioService] Seek verification (attempt ${retryCount + 1}):`, {
                requestedPosition: seekToTime,
                actualPosition,
                requestedPercentage: position,
                actualPercentage,
                difference,
                successful: seekSuccessful
              });
              
              return seekSuccessful;
            }
            
            return false;
          };
          
          let success = await attemptSeek();
          while (!success && retryCount < maxRetries) {
            retryCount++;
            console.log(`[audioService] Retrying seek (${retryCount}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, delayBetweenRetries));
            success = await attemptSeek();
          }
          
          return success;
        };
        
        // Start the seek process but don't await it (let it happen in background)
        seekWithRetries().then(success => {
          if (!success) {
            console.warn(`[audioService] Failed to seek to position ${position}% after multiple attempts`);
          }
        });
      } else if (position > 0) {
        console.warn(`[audioService] Cannot seek ${songId}: duration is ${duration}`);
      } else {
        console.log(`[audioService] Starting ${songId} from beginning (position = ${position})`);
      }

      return true;

    } catch (error) { 
      console.error(`[audioService] Error in playSong for ${songId}:`, error);
      this.callbacks.onError(songId, 'play', error.message || 'Unknown play error');
      this.isRefreshing.delete(songId);
      return false;
    }
  }

  /**
   * Pause current playback
   */
  pause() {
    if (this.currentSound) {
      this.currentSound.pause();
      this.stopProgressTracking();
    }
  }

  /**
   * Resume current playback
   * @returns {boolean} - Success status
   */
  resume() {
    if (this.currentSound && this.currentSongId) {
      this.currentSound.play();
      return true;
    }
    return false;
  }

  /**
   * Seek to position in current song
   * @param {number} percentage - Position in percentage (0-100)
   */
  seekTo(percentage) {
    console.log('[audioService] seekTo called with:', {
      percentage,
      currentSound: this.currentSound ? 'exists' : 'null',
      currentSongId: this.currentSongId,
    });
    
    if (!this.currentSound || !this.currentSongId) {
      console.warn('[audioService] Cannot seek: No sound is currently active');
      return;
    }

    const duration = this.currentSound.duration();
    const seekPosition = percentage * duration / 100;
    const songId = this.currentSongId;
    
    console.log('[audioService] Seeking to:', {
      percentage,
      duration,
      seekPositionSeconds: seekPosition,
      soundState: this.currentSound.state(),
    });
    
    // Enhanced seeking with retries
    const seekWithRetries = async (maxRetries = 2, delayBetweenRetries = 200) => {
      let retryCount = 0;
      
      const attemptSeek = async () => {
        // Check if we can still seek
        if (!this.currentSound || this.currentSongId !== songId) {
          console.log(`[audioService] Seek aborted - current song changed or sound no longer available`);
          return false;
        }
        
        console.log(`[audioService] Seeking to ${seekPosition}s (${percentage}%) - attempt ${retryCount + 1}`);
        this.currentSound.seek(seekPosition);
        
        // Verify seek was successful
        await new Promise(resolve => setTimeout(resolve, 150));
        
        if (this.currentSound) {
          const actualPosition = this.currentSound.seek();
          const actualPercentage = (actualPosition / duration) * 100;
          const difference = Math.abs(seekPosition - actualPosition);
          const seekSuccessful = difference < 0.5 || actualPosition > 0.5;
          
          console.log(`[audioService] Seek verification (attempt ${retryCount + 1}):`, {
            requestedPosition: seekPosition,
            actualPosition,
            requestedPercentage: percentage,
            actualPercentage,
            difference,
            successful: seekSuccessful
          });
          
          return seekSuccessful;
        }
        
        return false;
      };
      
      let success = await attemptSeek();
      while (!success && retryCount < maxRetries) {
        retryCount++;
        console.log(`[audioService] Retrying seek (${retryCount}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, delayBetweenRetries));
        success = await attemptSeek();
      }
      
      return success;
    };
    
    // Call the callback right away to update UI
    this.callbacks.onSeek(this.currentSongId, percentage);
    
    // Start the seek process but don't await it (let it happen in background)
    seekWithRetries().then(success => {
      if (!success) {
        console.warn(`[audioService] Failed to seek to position ${percentage}% after multiple attempts`);
      }
    });
  }

  /**
   * Get current progress
   * @returns {number} - Current progress in percentage (0-100)
   */
  getProgress() {
    if (!this.currentSound) return 0;
    
    const duration = this.currentSound.duration();
    if (!duration) return 0;
    
    return (this.currentSound.seek() / duration) * 100;
  }

  /**
   * Start tracking progress with throttling
   */
  startProgressTracking() {
    this.stopProgressTracking();
    
    this.progressInterval = setInterval(() => {
      if (!this.currentSound || !this.currentSongId) return;
      
      const now = Date.now();
      if (now - this.lastProgressUpdate >= this.progressThrottleMs) {
        const progress = this.getProgress();
        this.callbacks.onProgress(this.currentSongId, progress);
        this.lastProgressUpdate = now;
      }
    }, this.progressThrottleMs);
  }

  /**
   * Stop tracking progress
   */
  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  /**
   * Unload a specific song to free memory
   * @param {string} songId - Song ID to unload
   */
  unloadSong(songId) {
    if (this.sounds.has(songId)) {
      const { sound } = this.sounds.get(songId);
      
      // Stop if currently playing
      if (this.currentSongId === songId) {
        this.stopProgressTracking();
        this.currentSongId = null;
        this.currentSound = null;
      }
      
      // Unload and remove
      sound.unload();
      this.sounds.delete(songId);
    }
  }

  /**
   * Check if a song is currently playing
   * @param {string} songId - Song ID to check
   * @returns {boolean} - Whether the song is playing
   */
  isPlaying(songId) {
    if (!songId) {
      return this.currentSound ? this.currentSound.playing() : false;
    }
    
    return this.currentSongId === songId && this.currentSound && this.currentSound.playing();
  }

  /**
   * Get global volume
   * @returns {number} - Volume (0-1)
   */
  getVolume() {
    return Howler.volume();
  }

  /**
   * Set global volume
   * @param {number} volume - Volume (0-1)
   */
  setVolume(volume) {
    Howler.volume(Math.max(0, Math.min(1, volume)));
  }

  /**
   * Handle playback errors, typically by trying to refresh the signed URL.
   * @param {string} songId - Song ID
   * @param {string} filePath - Path to song file
   * @param {string} type - 'play' or 'load' error
   */
  async handlePlaybackError(songId, filePath, type) {
    console.warn(`Playback ${type} error for ${songId}, attempting to refresh URL.`);
    
    // This check is now primarily in onloaderror/onplayerror to prevent calling handlePlaybackError initially.
    // if (this.isRefreshing.get(songId)) {
    //   console.warn(`Already refreshing ${songId}, aborting duplicate attempt.`);
    //   return; 
    // }

    this.isRefreshing.set(songId, true); // Mark that a refresh for this songId has started.

    if (this.sounds.has(songId)) {
      const oldSoundData = this.sounds.get(songId);
      if (oldSoundData.sound) {
        oldSoundData.sound.unload();
      }
      this.sounds.delete(songId);
    }

    if (this.currentSongId === songId) {
      this.currentSound = null;
      this.currentSongId = null; 
      this.stopProgressTracking();
    }
    
    console.log(`Attempting to reload and play ${songId} after error.`);
    try {
      const lastPosition = this.lastAttemptedPositions.get(songId) || 0;
      // Call playSong WITH the retrieved position and mark it as a retry.
      const success = await this.playSong(songId, filePath, lastPosition, true); 
      
      if (success) {
        console.log(`Successfully reloaded and attempted to play ${songId} (at ${lastPosition}%) after ${type} error.`);
        // Don't clear isRefreshing here; let onload/onplay do it on actual success of the refreshed play.
      } else {
        console.error(`Failed to recover ${songId} after ${type} error and refresh attempt (playSong returned false).`);
        this.isRefreshing.delete(songId); // Clear flag as retry attempt itself failed.
      }
    } catch (e) {
        console.error(`Exception during handlePlaybackError's playSong call for ${songId}:`, e);
        this.isRefreshing.delete(songId); // Clear flag on exception during retry.
    } 
  }

  /**
   * Unload all sounds and reset
   */
  reset() {
    if (this.currentSound) {
      this.currentSound.stop();
      this.currentSound.unload();
    }
    this.sounds.forEach(({ sound }) => sound.unload());
    this.sounds.clear();
    this.isRefreshing.clear(); // Clear all refresh flags
    this.lastAttemptedPositions.clear(); // Added: Clear last attempted positions on reset
    this.currentSongId = null;
    this.currentSound = null;
    this.stopProgressTracking();
    console.log('AudioService reset');
  }
}

// Singleton instance
const audioService = new AudioService();
export default audioService; 